import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useContextualMenu } from '@databases/contextual-menu';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { getFieldType } from '@databases/fields';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import InlineEditWrapper from '@databases/inline-edit/InlineEditWrapper';
import { messages } from '@databases/intl/messages';
import { isApple } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  deselectAll,
  selectFieldMulti,
  selectFieldSingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import { type Adjacents } from '@databases/redux/cellSelections/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import FieldNameContainer from './FieldNameContainer';
import { FieldNameViewContainer } from './FieldNameViewContainer';

const StyledInlineEditWrapper = styled(InlineEditWrapper)`
  > ${FieldNameViewContainer} {
    padding: ${token('space.025')} ${token('space.050', '4px')}
      ${token('space.025', '2px')} ${token('space.050', '4px')};
  }
`;

export interface FieldNameHeaderProps {
  contentId: string;
  field: DatabaseField<DatabaseFieldType>;
  hoverDeleteButton?: boolean;
  isReadonly?: boolean;
  isSelected?: boolean;
  selectedAdjacentCells?: Adjacents;
  showIcon?: boolean;
  actionComp?: React.ReactNode;
  hideText?: boolean;
}

const fieldNameViewButtonContainerStyles = xcss({
  whiteSpace: 'pre',
  overflow: 'hidden',
  display: 'block',
});
const hideTextStyles = xcss({
  position: 'absolute',
  right: 'space.0',
});

const FieldNameHeader = forwardRef<HTMLInputElement, FieldNameHeaderProps>(
  function FieldNameHeader(
    {
      contentId,
      field,
      showIcon,
      isSelected,
      hoverDeleteButton,
      selectedAdjacentCells,
      isReadonly,
      actionComp,
      hideText,
    },
    _ref
  ) {
    const { showPanel } = usePanel();
    const { closeMenu } = useContextualMenu();
    const dispatch = defaultStoreUtils.useDispatch();
    const { formatMessage } = useIntl();
    const client = useAnalytics();

    const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
      'databases-frontend-visual-refresh-table-m1'
    );

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (isReadonly) {
          return;
        }

        // Prevent event from bubbling up to parent handlers on every click, instead of just
        // on clicks with shift key pressed. This ensures selection state updates are consistent.
        e.preventDefault();
        e.stopPropagation();

        const isMultiSelectOn = isModifierKeyPressed(e, [
          isApple ? 'metaKey' : 'ctrlKey',
          'shiftKey',
        ]);
        const isRangeSelectOn = isModifierKeyPressed(e, ['shiftKey']);

        if (isVisualRefreshM1Enabled && isRangeSelectOn) {
          blurActiveElement();
          removeTextSelection();

          dispatch(
            selectFieldMulti({
              contentId,
              fieldId: field.id,
              isRangeSelect: !isMultiSelectOn,
            })
          );
          dispatch(setSoftSelection({ value: false }));
          return;
        }

        dispatch(deselectAll());
        dispatch(
          selectFieldSingle({
            contentId,
            fieldId: field.id,
          })
        );
        dispatch(setSoftSelection({ value: true }));
        showPanel('FieldSettingsPanel', { fieldId: field.id });
        closeMenu();

        client.sendDatabaseFieldUpdatedEvent({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
        });
      },
      [
        isReadonly,
        isVisualRefreshM1Enabled,
        dispatch,
        contentId,
        field.id,
        showPanel,
        closeMenu,
        client,
      ]
    );

    const hasNonEmptyFieldName = useMemo(
      () => field.name && field.name !== '',
      [field.name]
    );

    return (
      <FieldNameContainer onClick={handleClick} data-testid="field-name-container">
        {showIcon && (
          <SkeletonItem isButton>
            <FieldTypeIcon
              type={field.type}
              label={getFieldType(field.type).label}
              noTooltip
            />
          </SkeletonItem>
        )}
        {/* TODO: we should bring selection related styles into this component to avoid using the inline edit wrapper */}
        <StyledInlineEditWrapper
          hideBorderOnFocus
          isDraggable={false}
          isSelected={isSelected}
          markAsDeleted={hoverDeleteButton}
          selectedAdjacentCells={selectedAdjacentCells}
        >
          <Flex direction="row" justifyContent="space-between" alignItems="center">
            <SkeletonItem minWidth={80}>
              <FieldNameViewContainer>
                {hasNonEmptyFieldName
                  ? field.name
                  : formatMessage(messages['field.default-field-name'])}
              </FieldNameViewContainer>
            </SkeletonItem>
            <Box xcss={[fieldNameViewButtonContainerStyles, hideText && hideTextStyles]}>
              {actionComp && <SkeletonItem minWidth={80}>{actionComp}</SkeletonItem>}
            </Box>
          </Flex>
        </StyledInlineEditWrapper>
      </FieldNameContainer>
    );
  }
);

export default FieldNameHeader;
